import React, { useState, useEffect } from 'react';
import {
    Box, Autocomplete, TextField, Stack, Typography,
    Card, CardContent, Grid, Button, Link
} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import IntegrationCard from './utility/IntegrationCard';


import { getSessionIdToken } from './auth/apiAuth'
import { retrieveSignedUrl } from './helpers/operations'

import axios from 'axios';

const INTEGRATIONS_ENDPOINT = `${process.env.REACT_APP_API_BASE_URL}/integrations`;

export default function Integrations({ auth }) {

    const [attachedProjects, setAttachedProjects] = useState(auth.userState.attached_projects);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState(null);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, serviceName: '', sucecss: true });
    const [serviceImagesMap, setServiceImagesMap] = useState({});

    useEffect(() => {
        retrieveSignedUrl({
            key: 'Pagination-WebApp/integrations-icons.json',
            bucket: 'documents.pagination.com',
            ssmParams: auth.userState.ssmParams,
        })
            .then(res => fetch(res.data.signedUrl))
            .then(res => res.json())
            .then(data => setServiceImagesMap(data))
            .catch(err => console.error('Error loading service icons'));
    }, []);

    //faccio il fetch dei projetti dell'utente all'avvio della pagina
    useEffect(() => {
        if (auth.userState.userItem) {
            const flagAllProjects = auth.userState.isAdmin || auth.userState.type === 'billing';
            if (flagAllProjects) {
                fetchProjects()
                    .then(response => {
                        setAttachedProjects(response.data.projectsList.map(el => `${el.customer}/${el.project_name}`));
                    });
            } else {
                setAttachedProjects(auth.userState.attached_projects);
                if (auth.userState.attached_projects.length === 1) {
                    handleChangeProject(undefined, auth.userState.attached_projects[0]);
                }
            }
        }
    }, []);

    //metodo per il fetch dei progetti
    const fetchProjects = async () => {
        return axios.get(`${process.env.REACT_APP_API_BASE_URL}/projects-webapp`, {
            headers: {
                'user_id': auth.userState.user,
                "content-type": "application/json",
                "Authorization": await getSessionIdToken(),
                "type": 'project-list',
            }
        });
    };

    //metodo per il fetch delle integrazioni di un progetto
    const fetchIntegrations = async (projectValue) => {
        const [customer, project_name] = projectValue.split('/').slice(0, 2);

        setLoading(true);
        setServices(null);
        setError(null);

        try {
            const response = await axios.get(INTEGRATIONS_ENDPOINT, {
                headers: {
                    'customer': customer,
                    'project_name': project_name,
                    "Authorization": await getSessionIdToken(),
                    'content-type': 'application/json',
                }
            });

            setServices(response.data.services || []);
        } catch (err) {
            const status = err.response?.status;
            if (status === 401) {
                setError({ type: 'auth' });
            } else if (status === 403) {
                setError({ type: 'no_support' });
            } else {
                setError({ type: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChangeProject = (event, value) => {
        if (!value) {
            setSelectedProject(null);
            setServices(null);
            setError(null);
            setLoading(false);
        } else {
            setSelectedProject(value);
            fetchIntegrations(value);
        }
    };

    const handleReset = () => {
        setSelectedProject(null);
        setServices(null);
        setError(null);
        setLoading(false);
    };

    //metodo per fare il patch delle integrazioniin S3 tramite API call
    const handleSaveService = async (serviceName, newParams) => {
        const [customer, project_name] = selectedProject.split('/').slice(0, 2);

        try {
            await axios.patch(INTEGRATIONS_ENDPOINT,
                { serviceName, parameters: newParams },
                {
                    headers: {
                        'Authorization': await getSessionIdToken(),
                        'customer': customer,
                        'project_name': project_name,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setSnackbar({ open: true, serviceName, success: true });
            fetchIntegrations(selectedProject);
        } catch (err) {
            console.error('Error updating service');
            setSnackbar({ open: true, serviceName, sucecss: false });
        }
    };

    return (
        <Box sx={{ margin: 0 }}>
            <AppBar position="sticky" sx={{ zIndex: 1 }}>
                <Stack direction='row' spacing={2} justifyContent='flex-start' alignItems='center'
                    sx={{ padding: '1em', background: 'white' }}
                >
                    <Autocomplete
                        id="project-selector"
                        options={attachedProjects}
                        sx={{ width: '100%', maxWidth: 600 }}
                        clearOnEscape
                        defaultValue={attachedProjects.length === 1 ? attachedProjects[0] : ''}
                        value={selectedProject}
                        onChange={handleChangeProject}
                        size='small'
                        renderInput={(params) => <TextField {...params} label="project" />}
                    />
                    {loading && (
                        <CircularProgress size="2em" />
                    )}
                </Stack>
            </AppBar>

            <Box sx={{ padding: '2em' }}>
                {!selectedProject && !error && (
                    <Typography variant='body1' color='text.secondary' sx={{ marginTop: '2em', textAlign: 'center' }}>
                        Select a project to view available integrations.
                    </Typography>
                )}

                {error && error.type === 'auth' && (
                    <Box sx={{ textAlign: 'center', marginTop: '2em' }}>
                        <Typography variant='body1' color='error'>
                            You do not have permission to access this integrations page.
                        </Typography>
                    </Box>
                )}

                {error && error.type === 'no_support' && (
                    <Box sx={{ textAlign: 'center', marginTop: '2em' }}>
                        <Typography variant='body1' color='text.secondary'>
                            This project does not support integrations. Contact{' '}
                            <Link href="mailto:support@pagination.com">support@pagination.com</Link>
                            {' '}for a quote.
                        </Typography>
                    </Box>
                )}

                {error && error.type === 'error' && (
                    <Box sx={{ textAlign: 'center', marginTop: '2em' }}>
                        <Typography variant='body1' color='error' sx={{ marginBottom: '1em' }}>
                            An unexpected error occurred. Please try again.
                        </Typography>
                        <Button variant='outlined' onClick={handleReset}>
                            Back
                        </Button>
                    </Box>
                )}

                {selectedProject && !loading && !error && services && services.length === 0 && (
                    <Box sx={{ textAlign: 'center', marginTop: '2em' }}>
                        <Typography variant='body1' color='text.secondary'>
                            No integrations available for this project.
                        </Typography>
                    </Box>
                )}

                {selectedProject && !loading && !error && services && services.length > 0 && (
                    <Box>
                        <Typography variant='h1' sx={{ marginBottom: '1em' }} fontSize={30} fontWeight={600}>
                            Integrations Available
                        </Typography>
                        <Grid container spacing={2} columns={12}>
                            {services.map((service, index) => (
                                <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }} >
                                    <IntegrationCard service={service} onSave={handleSaveService} serviceImagesMap={serviceImagesMap} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ open: false, serviceName: '' })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar({ open: false, serviceName: '' })}
                    severity={snackbar.success ? "success" : "error"}
                    variant="filled"
                >
                    {snackbar.success && (snackbar.serviceName+" params updated!")}
                    {!snackbar.success && ("An error occured in "+snackbar.serviceName+" update. Please try again.")}
                </Alert>
            </Snackbar>
        </Box>
    );
}